import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SxCalendarComponent } from '@core/components/sx-calendar/calendar';
import dayjs from 'dayjs';

/** Custom header component for datepicker. */
@Component({
    selector: 'app-custom-calendar-header',
    templateUrl: './custom-calendar-header.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CustomCalendarHeaderComponent<D> implements OnInit, OnDestroy {
  private destroyed = new Subject<void>();
  hideMonthMovement = false;

  disabledMinY = false;
  disabledMinM = false;
  disabledMaxY = false;
  disabledMaxM = false;
  periodLabel = '';

  constructor(
    private calendar: SxCalendarComponent<D>,
    private dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef
  ) {
    if (calendar.minDate) {
      let activeDate: any = calendar.activeDate;
      this.disabledMinM = dateAdapter.addCalendarDays(calendar.activeDate, -activeDate.getDate()) < calendar.minDate;
      this.disabledMinY = dateAdapter.addCalendarYears(calendar.activeDate, -1) < calendar.minDate;
    }

    if (calendar.maxDate && calendar.activeDate) {
      this.disabledMaxM = dateAdapter.addCalendarMonths(calendar.activeDate, 1) > calendar.maxDate;
      this.disabledMaxY = dateAdapter.addCalendarYears(calendar.activeDate, 1) > calendar.maxDate;
    }
    if (this.hideMonthMovement !== this.calendar.disableMonthView) {
      this.hideMonthMovement = this.calendar.disableMonthView;
    }

    calendar.stateChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
      if (this.hideMonthMovement !== this.calendar.disableMonthView) {
        this.hideMonthMovement = this.calendar.disableMonthView;
        cdr.detectChanges();
      }

      if (this.calendar.minDate) {
        let prevMonth = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
        let prevYear = this.dateAdapter.addCalendarYears(this.calendar.activeDate, -1);

        let prevYearYear = this.dateAdapter.getYear(prevYear);
        let minDateYear = this.dateAdapter.getYear(this.calendar.minDate);

        this.disabledMinM = this.compareMonth(prevMonth, this.calendar.minDate) < 0;
        this.disabledMinY = prevYearYear < minDateYear;
      }

      if (this.calendar.maxDate) {
        let nextMonth = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
        let nextYear = this.dateAdapter.addCalendarYears(this.calendar.activeDate, 1);
        let nextYearYear = this.dateAdapter.getYear(nextYear);
        let maxDateYear = this.dateAdapter.getYear(this.calendar.maxDate);

        this.disabledMaxM = this.compareMonth(nextMonth, this.calendar.maxDate) > 0;
        this.disabledMaxY = nextYearYear > maxDateYear;
      }
      this.generateLabel();
      cdr.markForCheck();
    });
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  generateLabel = () => {
    this.periodLabel = this.dateAdapter
      .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
      .toLocaleUpperCase();
  };

  previousClicked(mode: 'month' | 'year') {
    this.calendar.activeDate =
      mode === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
        : this.dateAdapter.addCalendarYears(this.calendar.activeDate, -1);
    this.generateLabel();
  }

  nextClicked(mode: 'month' | 'year') {
    this.calendar.activeDate =
      mode === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
        : this.dateAdapter.addCalendarYears(this.calendar.activeDate, 1);
    this.generateLabel();
  }

  compareMonth(dateA: any, dateB: any): number {
    let aAsNumber = +dayjs(dateA).format('YYYYMM');
    let bAsNumber = +dayjs(dateB).format('YYYYMM');

    if (aAsNumber === bAsNumber) {
      return 0;
    }

    return aAsNumber < bAsNumber ? -1 : 1;
  }
}
